import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, } from 'react';
import { InteractionManager, StyleSheet } from 'react-native';
import { createTransaction, createTransactionSchedule, editTransactions, getAccountGoals, getCategories, refreshApp, } from 'actions';
import Amplitude from 'utils/amplitude';
import Modal from 'utils/packages/Modal';
import { rem } from 'design-system/values';
import Indicator from 'design-system/Indicator';
import { AppView } from 'design-system/AppView';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useColors from 'hooks/useColors';
import { Tier } from 'features/premium/entities';
import { selectFeatureFlag, selectUserId } from 'reducers/selectors';
import { goBackWithKey } from 'utils/navigationv6';
import { benefits } from 'features/premium/benefits';
import useRefreshAccounts from 'hooks/useRefreshAccounts';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useFetchOld, { muteErrorAndResult } from 'hooks/useFetch';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import IconPlus from 'design-system/icons/IconPlus';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { isWeb } from '../../../constants';
import useDeleteCategory from '../hooks/useDeleteCategory';
import EditCategoryList from '../components/categories/EditCategoryList';
import useCheckTransactionsModal from '../hooks/useCheckTransactionsModal';
import EditCategoryModalContent from '../components/categories/EditCategoryModalContent';
import { ManageCategoriesModal } from '../components/ManageCategoriesModal';
const strings = {
    addTransaction: 'Add transaction',
    title: 'Pick a category',
    checkSimilarModalTitle: (cat) => `Which transactions should we update to ${cat}?`,
};
const EditCategoryScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation, route }) => {
    const colors = useColors();
    const { isPickingCategory, onSimplePickCategory, multipleEditing } = route.params;
    // The user is still picking a category if transaction is defined, they just use a different flow
    const { transaction } = route.params;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.PICK_CATEGORY}/>),
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const isFetching = useAppSelector((store) => store.user.isFetching && !store.user.categories?.length);
    const refreshAccounts = useRefreshAccounts();
    const [, , fetching, fetch] = useFetchOld(muteErrorAndResult);
    const fetchingRef = useRef(fetching);
    fetchingRef.current = fetching;
    const dispatch = useAppDispatch();
    const userId = useAppSelector(selectUserId);
    const { isUnlockedForSpace: isCustomCategoriesUnlockedForSpace, onPressUpgrade: onPressUpgradeForCustomCategories, } = useShouldShowUpgrade({ benefitId: 'customCategories' });
    const { isUnlockedForSpace: isAdvancedEditingUnlockedForSpace, onPressUpgrade: onPressUpgradeForAdvancedEditing, } = useShouldShowUpgrade({ benefitId: 'advancedTransactionsEditing' });
    const justThisTransactionCb = useCallback((transaction, data) => {
        Modal.hide();
        fetch(editTransactions({
            [transaction.id]: 'categoryId' in transaction
                ? transaction.categoryId
                : transaction.category.id,
        }, data));
        route.params.onEditCallBack?.(data);
        navigation.goBack();
    }, [route.params]);
    const checkSimilarTransactionsCb = useCallback((transaction, data) => {
        Modal.hide(() => {
            navigation.navigateDeprecated('EditMultipleTransactions', {
                category: data,
                isSelectedAll: true,
                keyBack: route.key,
                transaction,
                filterOut: data,
            });
        });
    }, [route.key]);
    const handleTransactionCategoryEdit = useCheckTransactionsModal(justThisTransactionCb, checkSimilarTransactionsCb, (_transaction, category) => ({
        title: strings.checkSimilarModalTitle(category.displayName),
    }));
    const onPressAdd = useCallback(() => {
        navigation.navigateDeprecated('CreateCustomCategory');
        Amplitude.logEvent('EditCategory.CustomCategorySetup');
    }, []);
    const upgradeRequired = useCallback((tier) => {
        if (tier === Tier.free)
            return;
        navigation.navigateDeprecated('EmmaProModal', {
            tier,
            isTierRequired: true,
        });
    }, []);
    const [, deleteCategory] = useDeleteCategory();
    const onLongPress = useCallback((data, isDefaultCategory) => {
        if ('customContent' in data) {
            navigation.navigateDeprecated('CreateCustomCategory');
        }
        else if (!isDefaultCategory) {
            const onEdit = () => {
                Modal.hide(() => {
                    if (!isCustomCategoriesUnlockedForSpace) {
                        upgradeRequired(benefits.customCategories.minTier);
                    }
                    else {
                        navigation.navigateDeprecated('CustomCategoryEdit', {
                            category: data,
                        });
                    }
                });
            };
            Modal.show(<EditCategoryModalContent onPressDelete={() => deleteCategory(data.id)} onPressEdit={onEdit}/>, {
                title: 'Edit',
                textStyle: styles.modalText,
                containerStyle: styles.modalContent,
            });
        }
    }, [deleteCategory, isCustomCategoriesUnlockedForSpace, upgradeRequired]);
    const isRecurringOfflineTransactionsEnabled = useAppSelector((state) => selectFeatureFlag(state, 'recurring_offline_transactions_v2').value);
    const pickCategoryForOfflineTransaction = useCallback((data) => {
        const bankAccount = route.params.data; // when isPickingCategory, data should always be defined
        const { amount, customName, keyBack } = route.params;
        if (bankAccount && amount !== undefined) {
            const currency = bankAccount.nativeBalance
                ? bankAccount.nativeBalance.currency
                : bankAccount.currency;
            navigation.navigateDeprecated('SelectDate', {
                amount: { amount, currency },
                showRecurringSwitch: isRecurringOfflineTransactionsEnabled && true,
                buttonText: strings.addTransaction,
                onSubmitDate: async (bookingDate, rrule, setIsSubmitting) => {
                    if (fetchingRef.current) {
                        return;
                    }
                    setIsSubmitting?.(true);
                    const body = {
                        amount,
                        categoryId: data.id,
                        accountId: bankAccount.id,
                        customName,
                        userId,
                        currency,
                        bookingDate,
                        updateAccountBalance: true,
                    };
                    const res = await fetch(rrule
                        ? createTransactionSchedule({
                            accountId: bankAccount.id,
                            counterpartName: customName || '',
                            categoryId: data.id,
                            startDate: new Date(bookingDate),
                            rrule,
                            amount,
                        })
                        : createTransaction(body));
                    if (res) {
                        fetch(getAccountGoals());
                        refreshAccounts(bankAccount.type);
                        navigation.navigateDeprecated('Success', {
                            title: 'Done!',
                            subTitle: rrule
                                ? 'Your scheduled transaction is active and will repeat automatically'
                                : 'Your transaction has been created!',
                            onContinue: () => {
                                if (keyBack) {
                                    goBackWithKey(navigation, keyBack);
                                }
                                else {
                                    navigation.goBack();
                                }
                                return true;
                            },
                        });
                    }
                    setIsSubmitting?.(false);
                },
            });
        }
        else {
            log('When navigating to EditCategoryScreen with isPickingCategory, data, amount should be defined', false, 'red');
        }
    }, [
        isRecurringOfflineTransactionsEnabled,
        refreshAccounts,
        route.params,
        userId,
    ]);
    const setCategoryOnMultipleTransactions = useCallback((data, isDefaultCategory) => {
        if (!isAdvancedEditingUnlockedForSpace) {
            onPressUpgradeForAdvancedEditing();
            return;
        }
        if (!isDefaultCategory && !isCustomCategoriesUnlockedForSpace) {
            onPressUpgradeForCustomCategories();
            return;
        }
        const { selected, onEditCallBack } = route.params;
        const editTxnObj = selected?.reduce((prev, curr) => ({
            ...prev,
            [curr.id]: curr.currentCategoryId,
        }), {});
        if (editTxnObj) {
            fetch(editTransactions(editTxnObj, data)).then((response) => {
                const { routes, index } = navigation.getState();
                if (!response && routes[index].name === 'LearningScreen') {
                    navigation.pop();
                    return;
                }
                dispatch(refreshApp());
            });
        }
        navigation.goBack();
        onEditCallBack?.(data);
    }, [
        isAdvancedEditingUnlockedForSpace,
        isCustomCategoriesUnlockedForSpace,
        onPressUpgradeForAdvancedEditing,
        onPressUpgradeForCustomCategories,
        route.params,
    ]);
    const setCategoryOnSingleTransaction = useCallback((transaction, data, isDefaultCategory) => {
        if (data.id === transaction.category?.id) {
            navigation.goBack();
            return;
        }
        if (!isDefaultCategory && !isCustomCategoriesUnlockedForSpace) {
            onPressUpgradeForCustomCategories();
            return;
        }
        handleTransactionCategoryEdit(transaction, data);
    }, [
        isCustomCategoriesUnlockedForSpace,
        handleTransactionCategoryEdit,
        onPressUpgradeForCustomCategories,
    ]);
    const onPressCategory = useCallback((data, isDefaultCategory) => {
        if ('customContent' in data) {
            onPressAdd();
            return;
        }
        if (onSimplePickCategory) {
            onSimplePickCategory(data);
            return;
        }
        if (isPickingCategory) {
            pickCategoryForOfflineTransaction(data);
            return;
        }
        if (multipleEditing) {
            setCategoryOnMultipleTransactions(data, isDefaultCategory);
            return;
        }
        if (transaction) {
            setCategoryOnSingleTransaction(transaction, data, isDefaultCategory);
            return;
        }
        // Not picking a category
        onLongPress(data, isDefaultCategory);
    }, [
        isPickingCategory,
        multipleEditing,
        onLongPress,
        onPressAdd,
        onSimplePickCategory,
        pickCategoryForOfflineTransaction,
        setCategoryOnMultipleTransactions,
        setCategoryOnSingleTransaction,
        transaction,
    ]);
    useEffect(() => {
        InteractionManager.runAfterInteractions(() => {
            dispatch(getCategories());
        });
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle, { paddingTop: rem(8) });
    const firstItem = useMemo(() => ({
        id: 'custom1',
        displayName: 'Add new',
        customContent: (<IconPlus width={rem(20)} height={rem(20)} color={colors.elements.brand}/>),
    }), [colors.elements.brand]);
    return (<AppView style={styles.content} withFrame={false}>
          {isFetching ? (<Indicator dark/>) : (<EditCategoryList scrollHandler={scrollHandler} selectedCategory={route.params.category} contentContainerStyle={contentContainerStyle} onPressCategory={onPressCategory} onLongPressCategory={onLongPress} onPressManage={ManageCategoriesModal.show} firstItem={firstItem} buttonTitle={multipleEditing || !!onSimplePickCategory ? 'Save' : 'Continue'}/>)}
        </AppView>);
}, ({ colors }) => ({
    title: strings.title,
    hideTitleOnScroll: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    content: {
        justifyContent: 'center',
    },
    modalText: {
        paddingHorizontal: rem(48),
        marginBottom: rem(30),
    },
    modalContent: {
        paddingHorizontal: 0,
    },
});
export default EditCategoryScreen;
